import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate } from "react-router-dom";
import Topbar from "../components/layout/Topbar.jsx";
import CompanySelector from "../components/ui/CompanySelector.jsx";
import { Pagination } from "../components/ui/index.jsx";
import { adminGetAccessLogGlobal } from "../services/credentialService.js";

// ─────────────────────────────────────────────
//  JOURNAL D'ACCÈS — page dédiée (admin)
//
//  Vue d'audit globale : toutes les consultations/copies de secrets
//  (mots de passe, champs personnalisés, codes 2FA) sur les credentials
//  d'une entreprise, ET (depuis le 15/09/2026, à la demande du client
//  "je veux l'élargir au ticket") toute l'activité sur ses tickets —
//  création, changement de statut/priorité, affectation, commentaires
//  et suppression. Un seul journal unifié, filtrable par "Cible" pour
//  ne voir que l'un ou l'autre. Répond à une demande d'audit (assurance
//  cyber, Loi 25) sans dépouiller credential par credential ni ticket
//  par ticket — voir CredentialController#adminGetAccessLogGlobal et
//  TicketController (points d'écriture des entrées TICKET).
//
//  Par défaut, TOUTES les entreprises confondues s'affichent (aucun
//  filtre actif) — l'admin peut ensuite restreindre à une entreprise via
//  CompanySelector (même composant que la page Mots de passe), et filtrer
//  par texte libre, type d'action, type de cible et plage de dates. Le
//  filtrage se fait côté serveur (pagination oblige — le journal peut
//  grossir vite) : chaque changement de filtre relance l'appel API.
//  Changé le 15/09/2026 : la page forçait jusque-là à choisir une
//  entreprise avant d'afficher quoi que ce soit — le client veut tout
//  voir d'emblée, avec ce filtre en option plutôt qu'obligatoire.
// ─────────────────────────────────────────────

const ACTION_OPTIONS = [
  { id: "",              label: "Toutes les actions" },
  { id: "VIEW",          label: "Consulté" },
  { id: "COPY",          label: "Copié" },
  { id: "CHANGE",        label: "Mot de passe modifié" },
  { id: "CREATE",        label: "Créé" },
  { id: "STATUS_CHANGE", label: "Statut modifié" },
  { id: "PRIORITY_CHANGE", label: "Priorité modifiée" },
  { id: "ASSIGN",        label: "Affectation" },
  { id: "COMMENT",       label: "Commentaire" },
  { id: "DELETE",        label: "Supprimé" },
];

const TARGET_OPTIONS = [
  { id: "",             label: "Toutes les cibles" },
  { id: "PASSWORD",     label: "Mot de passe" },
  { id: "CUSTOM_FIELD", label: "Champ personnalisé" },
  { id: "TOTP",         label: "Code 2FA" },
  { id: "TICKET",       label: "Ticket" },
];

const ACTION_LABELS = {
  VIEW: "Consulté", COPY: "Copié", CHANGE: "Modifié",
  CREATE: "Créé", STATUS_CHANGE: "Statut modifié", PRIORITY_CHANGE: "Priorité modifiée",
  ASSIGN: "Affectation", COMMENT: "Commentaire", DELETE: "Supprimé",
};
const TARGET_LABELS = {
  PASSWORD: "Mot de passe", CUSTOM_FIELD: "Champ personnalisé", TOTP: "Code 2FA", TICKET: "Ticket",
};
const ACTION_BADGE = {
  VIEW: "#4DABF7", COPY: "#E8412F", CHANGE: "#F59E0B",
  CREATE: "#10B981", STATUS_CHANGE: "#7C3AED", PRIORITY_CHANGE: "#F59E0B",
  ASSIGN: "#4DABF7", COMMENT: "#6B7280", DELETE: "#DC2626",
};

const PAGE_SIZE = 25;

function fmtDate(iso) {
  return new Date(iso).toLocaleString("fr-FR", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

export default function AccessLogPage() {

  const navigate = useNavigate();

  // Entreprise sélectionnée — null = toutes les entreprises (comportement
  // par défaut à l'ouverture, voir note en tête de fichier). Ce n'est plus
  // pré-rempli avec l'entreprise de l'utilisateur connecté : un SUPER_ADMIN
  // (seul rôle autorisé sur cette page) n'est de toute façon rattaché à
  // aucune entreprise.
  const [company, setCompany] = useState(null);

  const companyId = company?.id || null;

  // ── Filtres ───────────────────────────────────
  const [searchInput, setSearchInput] = useState("");   // valeur tapée, non débouncée
  const [search,      setSearch]      = useState("");   // valeur effective envoyée à l'API
  const [action,      setAction]      = useState("");
  const [targetType,  setTargetType]  = useState("");
  const [dateFrom,    setDateFrom]    = useState("");
  const [dateTo,      setDateTo]      = useState("");

  // Débounce la recherche texte pour ne pas déclencher un appel par frappe
  const searchDebounce = useRef(null);
  useEffect(() => {
    clearTimeout(searchDebounce.current);
    searchDebounce.current = setTimeout(() => setSearch(searchInput.trim()), 350);
    return () => clearTimeout(searchDebounce.current);
  }, [searchInput]);

  const hasActiveFilters = !!(company || search || action || targetType || dateFrom || dateTo);

  const resetFilters = () => {
    setCompany(null);
    setSearchInput(""); setSearch(""); setAction(""); setTargetType("");
    setDateFrom(""); setDateTo("");
  };

  // ── Données paginées ──────────────────────────
  const [page,     setPage]     = useState(0);
  const [entries,  setEntries]  = useState([]);
  const [totalElements, setTotalElements] = useState(0); // alimente aussi <Pagination/>, voir plus bas
  const [loading,  setLoading]  = useState(false);
  const [error,    setError]    = useState("");

  // Revenir à la première page dès qu'un filtre change (une page 4 qui
  // n'existe plus une fois filtré donnerait une liste vide déroutante)
  useEffect(() => { setPage(0); }, [companyId, search, action, targetType, dateFrom, dateTo]);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError("");
    adminGetAccessLogGlobal(companyId, {
      page, size: PAGE_SIZE, action: action || undefined, targetType: targetType || undefined,
      dateFrom: dateFrom || undefined, dateTo: dateTo || undefined, search: search || undefined,
    })
      .then(res => {
        if (cancelled) return;
        setEntries(Array.isArray(res?.content) ? res.content : []);
        setTotalElements(res?.totalElements ?? 0);
      })
      .catch(err => { if (!cancelled) setError(err.message); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [companyId, page, search, action, targetType, dateFrom, dateTo]);

  return (
    <>
      <Topbar title="Journal d'accès" subtitle={company?.name || "Toutes les entreprises"}/>
      <div className="page-body">

        {/* ── En-tête : sélecteur d'entreprise (optionnel, "Toutes les entreprises" par défaut) + compteur ── */}
        <div className="log-header anim-fade-up">
          <CompanySelector selectedId={companyId} onChange={setCompany} placeholder="Toutes les entreprises"/>
          <span className="log-count">
            {loading ? "Chargement…" : `${totalElements} entrée${totalElements > 1 ? "s" : ""}`}
          </span>
        </div>

            {/* ── Barre de filtres ── */}
            <div className="itm-card anim-fade-up log-filters" style={{ animationDelay: ".05s" }}>
              <div className="log-filter-row">
                <div className="log-search-wrap">
                  <i className="bi bi-search log-search-icon"/>
                  <input
                    className="log-search"
                    placeholder="Rechercher un identifiant, un utilisateur…"
                    value={searchInput}
                    onChange={e => setSearchInput(e.target.value)}
                  />
                  {searchInput && (
                    <button className="log-search-clear" onClick={() => setSearchInput("")}>
                      <i className="bi bi-x"/>
                    </button>
                  )}
                </div>

                <select className="log-select" value={action} onChange={e => setAction(e.target.value)}>
                  {ACTION_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>

                <select className="log-select" value={targetType} onChange={e => setTargetType(e.target.value)}>
                  {TARGET_OPTIONS.map(o => <option key={o.id} value={o.id}>{o.label}</option>)}
                </select>

                <div className="log-date-wrap">
                  <label>Du</label>
                  <input type="date" className="log-date" value={dateFrom}
                         max={dateTo || undefined}
                         onChange={e => setDateFrom(e.target.value)}/>
                </div>
                <div className="log-date-wrap">
                  <label>Au</label>
                  <input type="date" className="log-date" value={dateTo}
                         min={dateFrom || undefined}
                         onChange={e => setDateTo(e.target.value)}/>
                </div>

                {hasActiveFilters && (
                  <button className="log-reset-btn" onClick={resetFilters} title="Réinitialiser les filtres">
                    <i className="bi bi-x-circle me-1"/>Réinitialiser
                  </button>
                )}
              </div>
            </div>

            {/* ── Erreur ── */}
            {!loading && error && (
              <div className="pwd-empty">
                <i className="bi bi-exclamation-triangle" style={{ color: "#DC2626", opacity: 1 }}/>
                <p style={{ color: "#DC2626" }}>{error}</p>
              </div>
            )}

            {/* ── Chargement (première fois, liste encore vide) ── */}
            {loading && entries.length === 0 && !error && (
              <div className="pwd-empty">
                <i className="bi bi-arrow-repeat log-spin" style={{ fontSize: 32, opacity: .4 }}/>
                <p>Chargement…</p>
              </div>
            )}

            {/* ── Vide ── */}
            {!loading && !error && entries.length === 0 && (
              <div className="pwd-empty">
                <i className="bi bi-journal-x"/>
                <p>Aucune entrée trouvée</p>
                <small>{hasActiveFilters ? "Modifiez ou réinitialisez les filtres." : "Aucun accès journalisé pour l'instant."}</small>
              </div>
            )}

            {/* ── Tableau ── */}
            {entries.length > 0 && (
              <div className="itm-card anim-fade-up" style={{ animationDelay: ".1s", padding: 0, opacity: loading ? .6 : 1, transition: "opacity .15s" }}>
                <table className="log-table">
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Entreprise</th>
                      <th>Utilisateur</th>
                      <th>Action</th>
                      <th>Cible</th>
                      <th>Identifiant</th>
                      <th>IP</th>
                    </tr>
                  </thead>
                  <tbody>
                    {entries.map((e, i) => (
                      <tr key={i}>
                        <td className="log-date-cell">{fmtDate(e.accessedAt)}</td>
                        <td>{e.companyName || "—"}</td>
                        <td>{e.userName || "—"}</td>
                        <td>
                          <span className="log-action-badge" style={{ color: ACTION_BADGE[e.action], background: `${ACTION_BADGE[e.action]}18` }}>
                            {ACTION_LABELS[e.action] || e.action}
                          </span>
                        </td>
                        <td>
                          {TARGET_LABELS[e.targetType] || e.targetType}
                          {e.fieldName && <span className="log-field-name"> · {e.fieldName}</span>}
                          {e.detail && <div className="log-detail-sub">{e.detail}</div>}
                        </td>
                        <td>
                          {e.targetType === "TICKET" ? (
                            e.ticketId ? (
                              <button
                                type="button"
                                className="log-link-btn"
                                onClick={() => navigate(`/tickets/${e.ticketId}`)}
                                title="Voir le ticket"
                              >
                                {e.ticketTitle || "—"}
                              </button>
                            ) : (
                              <span className="log-deleted-ticket">
                                {e.ticketTitle || "—"} <em>(supprimé)</em>
                              </span>
                            )
                          ) : (
                            e.credentialName || "—"
                          )}
                        </td>
                        <td className="log-ip-cell">{e.ipAddress || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

        {/* ── Pagination ── */}
        {/* Composant partagé (même que la liste des tickets) : boutons de
            page numérotés, plus lisible que de simples flèches précédent/
            suivant maintenant que le journal peut compter beaucoup de
            pages (vue "toutes entreprises" par défaut). */}
        {totalElements > 0 && (
          <Pagination page={page} total={totalElements} pageSize={PAGE_SIZE} onChange={setPage}/>
        )}
      </div>
      <style>{CSS}</style>
    </>
  );
}

// ─────────────────────────────────────────────
//  STYLES
// ─────────────────────────────────────────────

const CSS = `
.cs-page-selector{display:flex;flex-direction:column;align-items:center;justify-content:center;padding:80px 20px;text-align:center}

/* En-tête */
.log-header{display:flex;align-items:center;justify-content:space-between;gap:12px;margin-bottom:14px;flex-wrap:wrap}
.log-count{font-size:12px;color:var(--text-3);white-space:nowrap}

/* Barre de filtres */
.log-filters{padding:14px 16px;margin-bottom:14px}
.log-filter-row{display:flex;align-items:center;gap:10px;flex-wrap:wrap}

.log-search-wrap{position:relative;display:flex;align-items:center;flex:1;min-width:220px}
.log-search-icon{position:absolute;left:10px;color:var(--text-3);font-size:13px;pointer-events:none}
.log-search{width:100%;background:var(--navy-mid);border:1px solid var(--border);border-radius:8px;color:var(--text-1);font-size:13px;padding:8px 32px;outline:none;transition:border-color .15s}
.log-search::placeholder{color:var(--text-3)}
.log-search:focus{border-color:var(--accent)}
.log-search-clear{position:absolute;right:8px;background:none;border:none;color:var(--text-3);cursor:pointer;font-size:14px;padding:0}

.log-select{background:var(--navy-mid);border:1px solid var(--border);border-radius:8px;color:var(--text-1);font-size:13px;padding:8px 10px;outline:none;cursor:pointer;transition:border-color .15s}
.log-select:focus{border-color:var(--accent)}

.log-date-wrap{display:flex;align-items:center;gap:6px}
.log-date-wrap label{font-size:11px;color:var(--text-3);white-space:nowrap}
.log-date{background:var(--navy-mid);border:1px solid var(--border);border-radius:8px;color:var(--text-1);font-size:13px;padding:7px 9px;outline:none;transition:border-color .15s}
.log-date:focus{border-color:var(--accent)}

.log-reset-btn{display:flex;align-items:center;background:none;border:1px solid var(--border);border-radius:8px;color:var(--text-2);font-size:12px;padding:7px 12px;cursor:pointer;white-space:nowrap;transition:all .15s}
.log-reset-btn:hover{border-color:var(--danger);color:var(--danger)}

/* Tableau */
.log-table{width:100%;border-collapse:collapse;font-size:13px}
.log-table thead th{text-align:left;font-size:10px;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:var(--text-3);padding:10px 16px;border-bottom:1px solid var(--border);background:rgba(0,0,0,.015)}
.log-table tbody td{padding:10px 16px;border-bottom:1px solid var(--border);color:var(--text-1);vertical-align:middle}
.log-table tbody tr:last-child td{border-bottom:none}
.log-table tbody tr:hover{background:rgba(0,0,0,.02)}
.log-date-cell{white-space:nowrap;color:var(--text-2);font-size:12.5px}
.log-ip-cell{font-family:monospace;font-size:12px;color:var(--text-3)}
.log-field-name{color:var(--text-3);font-size:12px}
.log-detail-sub{color:var(--text-3);font-size:11.5px;margin-top:2px}

.log-action-badge{display:inline-block;font-size:11px;font-weight:600;padding:2px 9px;border-radius:20px}

.log-link-btn{background:none;border:none;padding:0;color:var(--primary,#4DABF7);font-size:inherit;font-weight:500;cursor:pointer;text-decoration:underline;text-underline-offset:2px}
.log-link-btn:hover{opacity:.8}
.log-deleted-ticket{color:var(--text-3)}
.log-deleted-ticket em{font-style:normal;font-size:11px}

/* Pagination */

/* État vide (réutilise la même charte que les autres pages) */
.pwd-empty{display:flex;flex-direction:column;align-items:center;padding:60px 20px;color:var(--text-3);text-align:center}
.pwd-empty i{font-size:42px;margin-bottom:14px;opacity:.4}
.pwd-empty p{font-size:14px;font-weight:500;color:var(--text-2);margin:0 0 4px}
.pwd-empty small{font-size:12px}

@keyframes logSpin{to{transform:rotate(360deg)}}
.log-spin{animation:logSpin .7s linear infinite;display:inline-block}
`;